/**
 * AI-coded Home dashboard contracts (spec §12, ARCHITECTURE §10).
 *
 *  - MetricRequest / MetricResult: the ONLY data capability generated widget code has. The
 *    sandboxed runtime asks the parent page over postMessage, the parent validates with these
 *    schemas and calls `POST /api/v1/dashboards/metrics/evaluate` with the user's own token.
 *  - Bridge protocol (parent ⇄ widget-runtime iframe): strict, versioned message schemas.
 *  - REST DTOs for versions, builds, runs, diffs and health reports.
 *
 * Every request schema is strict: unknown keys (e.g. `tenantId`) are rejected, never ignored.
 */
import { z } from 'zod';
import { IsoDateSchema, IsoDateTimeSchema, UuidSchema } from './common';

export const DASHBOARD_SDK_VERSION = '1.0.0';

// ─── version / build lifecycle ──────────────────────────────────────────────
export const DASHBOARD_VERSION_STATUSES = [
  'draft',
  'building',
  'preview',
  'failed',
  'published',
  'archived',
] as const;
export type DashboardVersionStatus = (typeof DASHBOARD_VERSION_STATUSES)[number];

export const DASHBOARD_BUILD_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export type DashboardBuildStatus = (typeof DASHBOARD_BUILD_STATUSES)[number];

export const DASHBOARD_HEALTH = ['unknown', 'ok', 'error'] as const;
export type DashboardHealth = (typeof DASHBOARD_HEALTH)[number];

export const DASHBOARD_VALIDATION_STATUSES = ['ok', 'needs_validation'] as const;
export type DashboardValidationStatus = (typeof DASHBOARD_VALIDATION_STATUSES)[number];

/** Widget source files live under `widgets/` (PascalCase .tsx) or `lib/` (shared helpers). */
export const WIDGET_PATH_RE = /^(widgets|lib)\/[A-Z][A-Za-z0-9]{0,47}\.tsx$/;
export const WIDGET_ID_RE = /^[a-z][a-z0-9_]{0,47}$/;

export const WidgetDependenciesSchema = z.object({
  metrics: z.array(z.string().max(64)).max(40).default([]),
  /** Custom contact fields referenced (`field:<key>`). */
  fields: z.array(z.string().max(80)).max(40).default([]),
  /** Event types referenced in metric filters. */
  eventTypes: z.array(z.string().max(80)).max(40).default([]),
});
export type WidgetDependencies = z.infer<typeof WidgetDependenciesSchema>;

export const DashboardWidgetManifestSchema = z.object({
  id: z.string().regex(WIDGET_ID_RE),
  title: z.string().min(1).max(120),
  entry: z.string().regex(WIDGET_PATH_RE),
  /** Metric ids used (kept for backwards compatibility; mirrors dependencies.metrics). */
  metrics: z.array(z.string()).max(20).default([]),
  size: z.enum(['s', 'm', 'l', 'xl']).default('m'),
  /** Static dependencies extracted at build time (schema-change revalidation). */
  dependencies: WidgetDependenciesSchema.optional(),
});
export type DashboardWidgetManifest = z.infer<typeof DashboardWidgetManifestSchema>;

/** 12-column grid placement. `row` orders widgets top to bottom; `col` is the 1-based start. */
export const DashboardLayoutItemSchema = z.object({
  widgetId: z.string().regex(WIDGET_ID_RE),
  col: z.number().int().min(1).max(12),
  row: z.number().int().min(1).max(48),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(8),
});
export type DashboardLayoutItem = z.infer<typeof DashboardLayoutItemSchema>;

export const DashboardManifestSchema = z.object({
  sdkVersion: z.string(),
  columns: z.literal(12).default(12),
  /** Visual density chosen by the user ("πιο μίνιμαλ"); the runtime adapts spacing. */
  density: z.enum(['comfortable', 'compact']).default('comfortable'),
  layout: z.array(DashboardLayoutItemSchema).max(12),
  widgets: z.array(DashboardWidgetManifestSchema).max(12),
});
export type DashboardManifest = z.infer<typeof DashboardManifestSchema>;

// ─── metrics capability ─────────────────────────────────────────────────────
export const DASHBOARD_METRIC_IDS = [
  'revenue',
  'visits',
  'purchases',
  'events',
  'avg_ticket',
  'active_customers',
  'new_customers',
  'repeat_customers',
  'repeat_rate',
  'customers_total',
  'at_risk_customers',
  'top_customers',
  /** Sales lines (ADR-SCH-13): Σ line amount (cents), grouped like any series. */
  'item_revenue',
  /** Sales lines: Σ quantity. */
  'units_sold',
  /** «Τζίρος ανά κατηγορία»: item revenue per product category. */
  'revenue_by_category',
  /** «Top προϊόντα»: item revenue per product, largest first (`limit`). */
  'top_products',
  /** «Τεμάχια ανά προϊόν»: units per product, largest first (`limit`). */
  'units_by_product',
] as const;
export type DashboardMetricId = (typeof DASHBOARD_METRIC_IDS)[number];

export const TIME_PRESETS = [
  'today',
  'last_7_days',
  'last_30_days',
  'last_90_days',
  'this_month',
  'last_month',
  'this_year',
  'last_12_months',
  'all_time',
] as const;
export type TimePreset = (typeof TIME_PRESETS)[number];

export const TimeWindowSchema = z.union([
  z.strictObject({ preset: z.enum(TIME_PRESETS) }),
  z.strictObject({ from: IsoDateSchema, to: IsoDateSchema }),
]);
export type TimeWindow = z.infer<typeof TimeWindowSchema>;

/** Time buckets, categorical splits, or a custom enum/text field (`field:<key>`). */
export const GROUP_BY_FIXED = [
  'none',
  'day',
  'week',
  'month',
  'weekday',
  'event_type',
  'tag',
] as const;
export const GroupBySchema = z.union([
  z.enum(GROUP_BY_FIXED),
  z.string().regex(/^field:[a-z][a-z0-9_]{0,62}$/),
  /** An event property (`prop:<eventType>.<key>`): categories, products, payment methods… */
  z.string().regex(/^prop:[a-z][a-z0-9_]{0,63}\.[a-z][a-z0-9_]{0,47}$/),
]);
export type MetricGroupBy = z.infer<typeof GroupBySchema>;

/** Validated filter object (no free-form AST: every key is typed and bounded). */
export const MetricFiltersSchema = z.strictObject({
  eventType: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,62}$/)
    .optional(),
  tag: z.string().min(1).max(60).optional(),
  /** at_risk_customers: days without activity (default 60). */
  inactiveDays: z.number().int().min(7).max(730).optional(),
  /** at_risk_customers / top_customers: minimum lifetime visits+purchases (default 2 / 1). */
  minActivities: z.number().int().min(1).max(100).optional(),
  /**
   * Only events whose property `key` equals `value` (of `eventType`, or of the sales-lines event
   * for the item metrics), e.g. `{ key: 'product_category', value: 'Τρυπάνια' }`.
   */
  property: z
    .strictObject({
      key: z.string().regex(/^[a-z][a-z0-9_]{0,47}$/),
      value: z.union([z.string().min(1).max(120), z.number(), z.boolean()]),
    })
    .optional(),
});
export type MetricFilters = z.infer<typeof MetricFiltersSchema>;

export const MetricRequestSchema = z.strictObject({
  metricId: z.enum(DASHBOARD_METRIC_IDS),
  groupBy: GroupBySchema.optional(),
  timeWindow: TimeWindowSchema.optional(),
  filters: MetricFiltersSchema.optional(),
  compare: z.enum(['previous_period']).optional(),
  /** List metrics only (≤ 50). */
  limit: z.number().int().min(1).max(50).optional(),
});
export type MetricRequest = z.infer<typeof MetricRequestSchema>;

export const METRIC_UNITS = ['cents', 'count', 'ratio'] as const;
export type MetricUnit = (typeof METRIC_UNITS)[number];

export const MetricRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
});
export type MetricRow = z.infer<typeof MetricRowSchema>;

/** List metrics: minimized customer rows (first name + last initial, no contact details). */
export const MetricListItemSchema = z.object({
  key: z.string(),
  name: z.string(),
  lastActivityAt: IsoDateTimeSchema.nullable(),
  daysSince: z.number().int().nullable(),
  activities: z.number().int(),
  spendCents: z.number().int(),
  tags: z.array(z.string()).default([]),
});
export type MetricListItem = z.infer<typeof MetricListItemSchema>;

export const MetricResultSchema = z.object({
  metricId: z.enum(DASHBOARD_METRIC_IDS),
  kind: z.enum(['series', 'scalar', 'list']),
  unit: z.enum(METRIC_UNITS),
  groupBy: z.string(),
  rows: z.array(MetricRowSchema),
  items: z.array(MetricListItemSchema).default([]),
  total: z.number().nullable(),
  previous: z.number().nullable(),
  window: z.object({ from: IsoDateSchema, to: IsoDateSchema }).nullable(),
  timezone: z.string(),
  truncated: z.boolean(),
});
export type MetricResult = z.infer<typeof MetricResultSchema>;

export const MetricCatalogItemSchema = z.object({
  id: z.enum(DASHBOARD_METRIC_IDS),
  label: z.object({ el: z.string(), en: z.string() }),
  description: z.object({ el: z.string(), en: z.string() }),
  unit: z.enum(METRIC_UNITS),
  kind: z.enum(['series', 'scalar', 'list']),
  groupBy: z.array(z.string()),
  timeWindow: z.boolean(),
  /** False when this business records no data the metric needs (e.g. no purchase events). */
  available: z.boolean(),
});
export type MetricCatalogItem = z.infer<typeof MetricCatalogItemSchema>;

export const MetricCatalogSchema = z.object({
  items: z.array(MetricCatalogItemSchema),
  groupFields: z.array(z.object({ ref: z.string(), label: z.string() })),
  /** Event properties usable as `groupBy: 'prop:<event>.<key>'` (and in `filters.property`). */
  propertyGroups: z
    .array(
      z.object({
        ref: z.string(),
        label: z.string(),
        eventType: z.string(),
        /**
         * The event type carries a value: only then can value metrics (revenue, purchases,
         * avg_ticket) use this property. Sales-line properties go with the item metrics.
         */
        hasValue: z.boolean().default(true),
      }),
    )
    .default([]),
  eventTypes: z.array(z.object({ key: z.string(), label: z.string() })),
  timezone: z.string(),
});
export type MetricCatalog = z.infer<typeof MetricCatalogSchema>;

export const SegmentPreviewSchema = z.object({
  segmentId: UuidSchema,
  name: z.string(),
  count: z.number().int(),
  smsEligible: z.number().int(),
  emailEligible: z.number().int(),
});
export type SegmentPreview = z.infer<typeof SegmentPreviewSchema>;

// ─── bridge protocol (parent ⇄ sandboxed widget runtime) ────────────────────
export const BRIDGE_PROTOCOL = 'gh-widgets/1';
const Channel = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
const Nonce = z.string().regex(/^[A-Za-z0-9_-]{16,128}$/);
const RequestId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

export const BridgeErrorSchema = z.object({
  code: z.enum(['invalid_request', 'rate_limited', 'forbidden', 'unavailable', 'not_found']),
  message: z.string().max(300),
});

/** Messages the PARENT sends to the runtime. */
export const ParentToRuntimeMessageSchema = z.discriminatedUnion('type', [
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:init'),
    channel: Channel,
    nonce: Nonce,
    theme: z.enum(['light', 'dark']),
    locale: z.enum(['el', 'en']),
    timezone: z.string().max(64),
    mode: z.enum(['live', 'preview']),
    versionId: UuidSchema,
    bundle: z.string().max(400_000),
    bundleHash: z.string().regex(/^[a-f0-9]{64}$/),
    manifest: DashboardManifestSchema,
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:metric.result'),
    channel: Channel,
    nonce: Nonce,
    requestId: RequestId,
    ok: z.boolean(),
    result: MetricResultSchema.optional(),
    error: BridgeErrorSchema.optional(),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:segment.result'),
    channel: Channel,
    nonce: Nonce,
    requestId: RequestId,
    ok: z.boolean(),
    result: SegmentPreviewSchema.optional(),
    error: BridgeErrorSchema.optional(),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:theme'),
    channel: Channel,
    nonce: Nonce,
    theme: z.enum(['light', 'dark']),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:ping'),
    channel: Channel,
    nonce: Nonce,
  }),
]);
export type ParentToRuntimeMessage = z.infer<typeof ParentToRuntimeMessageSchema>;

/** Messages the RUNTIME sends to the parent (validated by the parent; never trusted). */
export const RuntimeToParentMessageSchema = z.discriminatedUnion('type', [
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:ready'),
    channel: Channel,
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:rendered'),
    channel: Channel,
    nonce: Nonce,
    widgets: z.array(z.object({ id: z.string().max(64), ok: z.boolean() })).max(24),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:error'),
    channel: Channel,
    nonce: Nonce,
    fatal: z.boolean(),
    widgetId: z.string().max(64).optional(),
    message: z.string().max(500),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:metric.request'),
    channel: Channel,
    nonce: Nonce,
    requestId: RequestId,
    // Validated separately (MetricRequestSchema) so a bad request gets an error reply.
    request: z.unknown(),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:segment.request'),
    channel: Channel,
    nonce: Nonce,
    requestId: RequestId,
    segmentId: z.unknown(),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:resize'),
    channel: Channel,
    nonce: Nonce,
    height: z.number().min(0).max(20_000),
  }),
  z.strictObject({
    protocol: z.literal(BRIDGE_PROTOCOL),
    type: z.literal('gh:pong'),
    channel: Channel,
    nonce: Nonce,
  }),
]);
export type RuntimeToParentMessage = z.infer<typeof RuntimeToParentMessageSchema>;

// ─── build reports ──────────────────────────────────────────────────────────
export const BUILD_STAGES = [
  'limits',
  'parse',
  'policy',
  'typecheck',
  'bundle',
  'store',
  'timeout',
  'internal',
] as const;
export type BuildStage = (typeof BUILD_STAGES)[number];

export const POLICY_CODES = [
  'invalid_path',
  'too_many_files',
  'file_too_large',
  'total_too_large',
  'syntax_error',
  'forbidden_import',
  'relative_import',
  'dynamic_import',
  'forbidden_global',
  'forbidden_property',
  'computed_access',
  'forbidden_element',
  'forbidden_attribute',
  'spread_attribute',
  'forbidden_syntax',
  'forbidden_new',
  'missing_default_export',
  'type_error',
  'bundle_error',
  'bundle_too_large',
  'timeout',
  'internal',
] as const;
export type PolicyCode = (typeof POLICY_CODES)[number];

export const BuildIssueSchema = z.object({
  code: z.enum(POLICY_CODES),
  file: z.string().nullable(),
  line: z.number().int().nullable(),
  column: z.number().int().nullable(),
  message: z.string(),
  snippet: z.string().nullable().default(null),
});
export type BuildIssue = z.infer<typeof BuildIssueSchema>;

export const BuildReportSchema = z.object({
  ok: z.boolean(),
  stage: z.enum(BUILD_STAGES).nullable(),
  issues: z.array(BuildIssueSchema),
  sourceBytes: z.number().int(),
  bundleBytes: z.number().int().nullable(),
  durationMs: z.number().int(),
  steps: z.array(
    z.object({ stage: z.enum(BUILD_STAGES), ok: z.boolean(), durationMs: z.number().int() }),
  ),
});
export type BuildReport = z.infer<typeof BuildReportSchema>;

// ─── REST DTOs ──────────────────────────────────────────────────────────────
export const DashboardVersionSummarySchema = z.object({
  id: UuidSchema,
  number: z.number().int(),
  status: z.enum(DASHBOARD_VERSION_STATUSES),
  parentVersionId: UuidSchema.nullable(),
  prompt: z.string().nullable(),
  changeSummary: z.string().nullable(),
  widgets: z.array(z.object({ id: z.string(), title: z.string() })),
  bundleHash: z.string().nullable(),
  schemaVersion: z.number().int().nullable(),
  health: z.enum(DASHBOARD_HEALTH),
  isActive: z.boolean(),
  isLastKnownGood: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
});
export type DashboardVersionSummary = z.infer<typeof DashboardVersionSummarySchema>;

export const DashboardBuildSchema = z.object({
  id: UuidSchema,
  status: z.enum(DASHBOARD_BUILD_STATUSES),
  report: BuildReportSchema.nullable(),
  startedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
});
export type DashboardBuild = z.infer<typeof DashboardBuildSchema>;

export const DashboardVersionDetailSchema = DashboardVersionSummarySchema.extend({
  manifest: DashboardManifestSchema.nullable(),
  sourceFiles: z.record(z.string(), z.string()),
  buildLog: z.string().nullable(),
  builds: z.array(DashboardBuildSchema),
  healthError: z.string().nullable(),
});
export type DashboardVersionDetail = z.infer<typeof DashboardVersionDetailSchema>;

export const DashboardValidationSchema = z.object({
  status: z.enum(DASHBOARD_VALIDATION_STATUSES),
  schemaVersion: z.number().int(),
  validatedSchemaVersion: z.number().int().nullable(),
  broken: z.array(
    z.object({
      widgetId: z.string(),
      title: z.string(),
      missing: z.array(z.string()),
    }),
  ),
});
export type DashboardValidation = z.infer<typeof DashboardValidationSchema>;

export const DashboardStateSchema = z.object({
  dashboardId: UuidSchema.nullable(),
  activeVersionId: UuidSchema.nullable(),
  lastKnownGoodVersionId: UuidSchema.nullable(),
  validation: DashboardValidationSchema.nullable(),
  versions: z.array(DashboardVersionSummarySchema),
  openRun: z.object({ runId: UuidSchema, versionId: UuidSchema }).nullable(),
  canPublish: z.boolean(),
});
export type DashboardState = z.infer<typeof DashboardStateSchema>;

export const ActiveVersionSchema = z.object({
  id: UuidSchema,
  number: z.number().int(),
  manifest: DashboardManifestSchema,
  bundleHash: z.string(),
  health: z.enum(DASHBOARD_HEALTH),
});
export type ActiveVersion = z.infer<typeof ActiveVersionSchema>;

export const ActiveDashboardSchema = z.object({
  active: ActiveVersionSchema.nullable(),
  /** Last known good version to fall back to when the active one fails at runtime. */
  fallback: ActiveVersionSchema.nullable(),
  validation: DashboardValidationSchema.nullable(),
});
export type ActiveDashboard = z.infer<typeof ActiveDashboardSchema>;

export const GenerateDashboardRequestSchema = z.strictObject({
  prompt: z.string().trim().min(2).max(1000),
  requestId: UuidSchema.optional(),
  /** Version to start from (default: the active one, else empty). */
  baseVersionId: UuidSchema.optional(),
});
export type GenerateDashboardRequest = z.infer<typeof GenerateDashboardRequestSchema>;

export const GenerateDashboardResponseSchema = z.object({
  runId: UuidSchema,
  versionId: UuidSchema,
  conversationId: UuidSchema,
  replayed: z.boolean(),
});
export type GenerateDashboardResponse = z.infer<typeof GenerateDashboardResponseSchema>;

export const DashboardRunSchema = z.object({
  runId: UuidSchema,
  state: z.string(),
  versionId: UuidSchema.nullable(),
  version: DashboardVersionSummarySchema.nullable(),
  /** Persisted agent events after `after` (AgentStreamEnvelope; validated loosely by clients). */
  events: z.array(z.record(z.string(), z.unknown())),
  lastSeq: z.number().int(),
  creditsCaptured: z.number().int(),
  awaitingApproval: z.boolean(),
});
export type DashboardRun = z.infer<typeof DashboardRunSchema>;

export const DashboardMessageSchema = z.object({
  id: UuidSchema,
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  versionId: UuidSchema.nullable(),
  runId: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type DashboardMessage = z.infer<typeof DashboardMessageSchema>;

export const DashboardConversationSchema = z.object({
  conversationId: UuidSchema.nullable(),
  messages: z.array(DashboardMessageSchema),
});
export type DashboardConversation = z.infer<typeof DashboardConversationSchema>;

export const HealthReportRequestSchema = z.strictObject({
  versionId: UuidSchema,
  ok: z.boolean(),
  mode: z.enum(['live', 'preview']).default('live'),
  errors: z
    .array(
      z.strictObject({ widgetId: z.string().max(64).optional(), message: z.string().max(300) }),
    )
    .max(24)
    .default([]),
});
export type HealthReportRequest = z.infer<typeof HealthReportRequestSchema>;

export const DiffLineSchema = z.object({ op: z.enum([' ', '+', '-']), text: z.string() });
export const DiffFileSchema = z.object({
  path: z.string(),
  status: z.enum(['added', 'removed', 'changed', 'same']),
  added: z.number().int(),
  removed: z.number().int(),
  hunks: z.array(
    z.object({
      fromLine: z.number().int(),
      toLine: z.number().int(),
      lines: z.array(DiffLineSchema),
    }),
  ),
});
export const DashboardDiffSchema = z.object({
  fromVersionId: UuidSchema.nullable(),
  toVersionId: UuidSchema,
  files: z.array(DiffFileSchema),
  layoutChanged: z.boolean(),
});
export type DashboardDiff = z.infer<typeof DashboardDiffSchema>;
